// SPDX-FileCopyrightText: 2025 Land Baden-Württemberg <InnoLab@stm.bwl.de>
//
// SPDX-License-Identifier: MPL-2.0

import { get } from 'svelte/store';
import { getApiUrl } from '../../config';
import { get as keycloakget, post } from '../../utils/CoreService';
import { BackendError, HttpError, JobDeletedError, JobMissingError, ValidationError } from '../../utils/Errors.js';
import { assertInstanceOf, assertString } from '../../utils/assertType';
import {
  SingleTranscript,
  TranscriptionJob,
  joberror_any,
  joberror_backend,
  joberror_deleted,
  joberror_http,
  joberror_missing,
  jobstatus_done,
  jobstatus_pending,
  jobstatus_transcribing,
  jobstatus_transcription_downloaded,
  jobstatus_translating,
} from './TranscriptionJobs.js';
import { TranscriptionJobsStore } from './TranscriptionJobsStore.js';

/**
 * Definition of the possible status that the backend will send to the frontend
 */
const backend_jobstatus_uploaded = 'uploaded';
const backend_jobstatus_prepared = 'prepared';
const backend_jobstatus_queued_ingress = 'queued_ingress';
const backend_jobstatus_dispatched = 'dispatched';
const backend_jobstatus_transcribing = 'transcribing';
const backend_jobstatus_translating = 'translating';
const backend_jobstatus_done = 'done';
const backend_jobstatus_deleted = 'deleted';
const backend_jobstatus_error = 'error';
const backend_jobstatus_non_existent = 'non_existent';

/** Manages the regular polling to the backend */
export class TranscriptionPolling {
  /**
   * @param {integer} interval - the interval in ms between two ticks
   */
  constructor(interval = 2000) {
    this.interval = interval;
    this.timer = null;
  }

  start() {
    if (this.timer) return;
    this.timer = setInterval(() => this.tick(), this.interval);
  }

  stop() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  /**
   * With every tick 20 jobs are collected and polled on parallel
   */
  async tick() {
    try {
      await this.pollJobs();
    } catch (error) {
      console.error('Error inside tick() during pollJobs()', error);
    }

    try {
      await this.downloadTranscriptions();
    } catch (error) {
      console.error('Error inside tick() during downloadTranscriptions()', error);
    }
  }

  async pollJobs() {
    let jobs = this.getJobsForPolling();
    if (jobs.length) {
      let list_job_ids = [];
      for (const job of jobs) {
        list_job_ids.push(job.getJobId());
      }

      const url = `${getApiUrl()}/transcription/check_transcription_exists`;
      const response = await post(url, list_job_ids, {
        contentType: 'application/json',
      });
      if (!response.ok) {
        throw new HttpError(`HTTP error ${response.status}: ${response.statusText}`, response.status);
      }

      const data = await response.json();

      for (const [jobId, jobStatus] of Object.entries(data.response)) {
        const jobToUpdate = jobs.find((job) => job.getJobId() === jobId);
        try {
          if (!jobToUpdate) {
            console.warn(`Job with id ${jobId} not found in local store`);
            continue; // skip
          }
          await this.checkTranscriptionStatus(jobStatus, jobToUpdate);
        } catch (error) {
          this.errorHandling(jobToUpdate, error);
        }
      }
    }
  }

  async downloadTranscriptions() {
    let jobs = this.getJobsReadyForDownload();
    if (jobs.length) {
      await Promise.all(
        jobs.map(async (job) => {
          try {
            await this.downloadTranscript(job);
          } catch (error) {
            this.errorHandling(job, error);
          }
        }),
      );
    }
  }

  getJobsReadyForDownload(limit = 20) {
    const jobs = get(TranscriptionJobsStore);
    const result = [];

    for (const job of jobs) {
      if (!job.hasError() && job.getJobStatus() === jobstatus_done) {
        result.push(job);
        if (result.length >= limit) break;
      }
    }

    return result;
  }

  getJobsForPolling(limit = 20) {
    const jobs = get(TranscriptionJobsStore);
    const result = [];

    for (const job of jobs) {
      if (!job.hasError() && job.isInProcess()) {
        result.push(job);
        if (result.length >= limit) break;
      }
    }

    return result;
  }

  /**
   * Check the status of the transcription for a job
   *
   * @param {string} jobStatus
   * @param {TranscriptionJob} job
   */
  async checkTranscriptionStatus(jobStatus, job) {
    assertString(jobStatus, 'jobStatus');
    assertInstanceOf(job, TranscriptionJob, 'job');

    switch (jobStatus) {
      case backend_jobstatus_non_existent:
        throw new JobMissingError(`No transcription job with id ${job.getJobId()} found.`);
      case backend_jobstatus_error:
        throw new BackendError('There was an error during the processing on the server.');
      case backend_jobstatus_deleted:
        throw new JobDeletedError(`The job with id ${job.getJobId()} was already deleted on the server.`);
      case backend_jobstatus_uploaded:
      case backend_jobstatus_prepared:
      case backend_jobstatus_queued_ingress:
      case backend_jobstatus_dispatched:
        job.setStatus(jobstatus_pending);
        TranscriptionJobsStore.updateJob(job);
        break;
      case backend_jobstatus_transcribing:
        job.setStatus(jobstatus_transcribing);
        TranscriptionJobsStore.updateJob(job);
        break;
      case backend_jobstatus_translating:
        job.setStatus(jobstatus_translating);
        TranscriptionJobsStore.updateJob(job);
        break;
      case backend_jobstatus_done:
        job.setStatus(jobstatus_done);
        TranscriptionJobsStore.updateJob(job);
        break;
      default:
        // Unknown in-process states should not interrupt polling.
        console.warn(`Ignoring unknown backend job status while polling: ${jobStatus}`);
    }
  }

  /**
   * Download the transcription for a job from the backend
   *
   * @param TranscriptionJob job
   */
  async downloadTranscript(job) {
    const url = `${getApiUrl()}/transcription/retrieve_transcription/${job.getJobId()}`;
    const response = await keycloakget(url);
    if (!response.ok) {
      throw new HttpError(`HTTP error ${response.status}: ${response.statusText}`);
    }

    const transcript_data = await response.json();
    const transcriptPayload = transcript_data.transcript;
    if (transcriptPayload && transcriptPayload.result) {
      const resultPayload = transcriptPayload.result;
      if (
        typeof resultPayload !== 'object' ||
        resultPayload === null ||
        !Array.isArray(resultPayload.segments) ||
        resultPayload.segments.some(
          (segment) =>
            typeof segment !== 'object' ||
            segment === null ||
            Array.isArray(segment) ||
            !Array.isArray(segment.subsegments),
        )
      ) {
        throw new ValidationError('Unexpected structure of the response from the server.');
      }

      let matched_terms = {};
      if (transcriptPayload.matched_terms) {
        matched_terms = transcriptPayload.matched_terms;
      }

      const translation_segments = Array.isArray(transcriptPayload.translation)
        ? transcriptPayload.translation.map((segment) => (typeof segment === 'string' ? segment : ''))
        : [];

      let new_transcription = new SingleTranscript(
        job.getJobId(),
        resultPayload,
        null,
        matched_terms,
        translation_segments,
        transcriptPayload.language ?? null,
      );
      job.setTranscription(new_transcription);
      job.setStatus(jobstatus_transcription_downloaded);
      TranscriptionJobsStore.updateJob(job);
    } else {
      throw new ValidationError('Unexpected structure of the response from the server.');
    }
  }

  /**
   * Handling of any error which may occur during the polling
   *
   * @param TranscriptionJob job
   * @param Error error
   */
  errorHandling(job, error) {
    console.error('Job failed:', job.getJobId(), error);
    let joberror_type = '';
    let joberror_message = error.message;

    if (error instanceof HttpError) {
      joberror_type = joberror_http;
    } else if (error instanceof JobMissingError) {
      joberror_type = joberror_missing;
      joberror_message = `Das Backend kennt die genutzte Job-Id ${job.getJobId()} nicht.`;
    } else if (error instanceof BackendError) {
      joberror_type = joberror_backend;
      joberror_message = 'Es gab einen Fehler beim Verarbeiten der Datei im Backend.';
    } else if (error instanceof JobDeletedError) {
      joberror_type = joberror_deleted;
      joberror_message = 'Die Datei und die Transkription wurden vom Server gelöscht.';
    } else {
      joberror_type = joberror_any;
    }

    job.setError(joberror_type, joberror_message);
    TranscriptionJobsStore.updateJob(job);
  }
}
